import type { AppEnv } from '@hc-frontend/core-context';
import { createLogger, format, transports } from 'winston';

import type {
  PerformSafeLogRepository,
  RequestContextRepository,
} from '../../domain';
import { getDomainIds, getIds } from '../utils/utils';

type DatadogServerLogAdapterOptions = {
  serviceId: string;
  apiKey: string;
  env: AppEnv;
  version: string;
};

export function datadogServerContextAdapterFactory(): RequestContextRepository {
  return async () => {
    const { cookies, headers } = await import('next/headers');
    const requestCookies = cookies();
    const requestHeaders = headers();

    const domain = getDomainIds(requestCookies);
    const ids = getIds(requestHeaders);

    return {
      response: {
        sessionId: ids.sessionId,
        userId: ids.userId,
      },
      request: {
        domainSessionId: domain.domainSessionId,
        domainUserId: domain.domainUserId,
        ipAddress:
          requestHeaders.get('x-forwarded-for') ||
          requestHeaders.get('x-real-ip') ||
          undefined,
        sessionId: requestCookies.get('healthcareSession')?.value,
        userId: requestCookies.get('healthcareUser')?.value,
        url: requestHeaders.get('x-url') ?? undefined,
        userAgent: requestHeaders.get('user-agent') ?? undefined,
      },
    };
  };
}

export const datadogServerLogAdapter = (
  options: DatadogServerLogAdapterOptions,
): PerformSafeLogRepository => {
  const httpTransportOptions: transports.HttpTransportOptions = {
    host: 'http-intake.logs.datadoghq.com',
    path: `/api/v2/logs?ddsource=apollo-server&service=${options.serviceId}&ddtags=env:${options.env},version:${options.version}`,
    ssl: true,
    headers: {
      'DD-API-KEY': options.apiKey,
    },
  };

  const logger = createLogger({
    exitOnError: false,
    format: format.json(),
    transports: [new transports.Http(httpTransportOptions)],
  });

  return async (log) => {
    logger[log.type](log.message, {
      ...log.data,
      source: log.context,
    });
  };
};
